import React from 'react';
import { useCart } from '../../state/cart/CartContext';

function formatGBP(n: number) {
  return `£ ${n.toFixed(2)}`;
}

type PromoCodeInputProps = {
  applyButtonClassName?: string;
};

export function PromoCodeInput({ applyButtonClassName = 'btn btn-outline-dark' }: PromoCodeInputProps) {
  const cart = useCart();
  const [promo, setPromo] = React.useState(cart.state.promoCode ?? '');
  const [promoError, setPromoError] = React.useState<string | null>(null);
  const [promoNotice, setPromoNotice] = React.useState<string | null>(null);
  const [promoBusy, setPromoBusy] = React.useState(false);

  React.useEffect(() => {
    setPromo(cart.state.promoCode ?? '');
  }, [cart.state.promoCode]);

  React.useEffect(() => {
    if (!cart.state.promoCode) {
      setPromoNotice(null);
      return;
    }
    if (cart.discount > 0) {
      setPromoNotice(`Promo ${cart.state.promoCode} applied — you save ${formatGBP(cart.discount)}`);
      setPromoError(null);
      return;
    }
    setPromoNotice('Promo code saved, but none of the items in your cart are eligible for a discount.');
  }, [cart.discount, cart.state.promoCode]);

  return (
    <div>
      <div className="fw-semibold mb-2">I have a promo code?</div>
      <div className="d-flex gap-2">
        <input
          value={promo}
          onChange={(e) => setPromo(e.target.value.toUpperCase())}
          className="form-control chunkzters-input"
          placeholder="Enter code"
          autoCapitalize="characters"
        />
        <button
          type="button"
          className={applyButtonClassName}
          disabled={promoBusy}
          onClick={async () => {
            if (!promo.trim()) {
              cart.clearPromoCode();
              setPromoError(null);
              setPromoNotice(null);
              return;
            }
            setPromoBusy(true);
            setPromoError(null);
            setPromoNotice(null);
            const res = await cart.applyPromoCode(promo.trim());
            setPromoBusy(false);
            if (res.error) {
              setPromoError(res.error);
              setPromoNotice(null);
              return;
            }
            if (res.code && (res.discountAmount ?? 0) > 0) {
              setPromoNotice(`Promo ${res.code} applied — you save ${formatGBP(res.discountAmount ?? 0)}`);
            } else if (res.code) {
              setPromoNotice('Promo code saved, but none of the items in your cart are eligible for a discount.');
            }
          }}
        >
          {promoBusy ? 'Applying...' : 'Apply'}
        </button>
      </div>
      {promoError ? <div className="small text-danger mt-2">{promoError}</div> : null}
      {!promoError && promoNotice ? (
        <div className="small mt-2" style={{ color: 'var(--chunkzters-ink-muted)' }}>
          {promoNotice}
        </div>
      ) : null}
    </div>
  );
}
